import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * OpStatusBadge — статус операции (planned / overdue / in_progress / done_full / done_partial / cancelled).
 *
 * Перенесено из frontend/src/components/OpStatusBadge.tsx (AntD Tag) — тут shadcn-style chip
 * с цветами Earth Tech:
 *   planned       → muted (нейтральный)
 *   overdue       → destructive (красный)
 *   in_progress   → primary (зелёный)
 *   done_full     → ndvi-good (тёмно-зелёный)
 *   done_partial  → accent-brand (янтарь)
 *   cancelled     → muted с зачёркиванием
 *
 * Если rescheduled=true — рядом ещё один янтарный chip «Перенесена» с тултипом.
 */
interface Props {
  status?: string | null;
  rescheduled?: boolean;
}

interface StatusStyle {
  label: string;
  className: string;
}

const STATUS_MAP: Record<string, StatusStyle> = {
  planned: {
    label: 'Запланирована',
    className: 'bg-muted text-foreground',
  },
  overdue: {
    label: 'Просрочена',
    className: 'bg-destructive/15 text-destructive',
  },
  in_progress: {
    label: 'Выполняется',
    className: 'bg-primary/15 text-primary',
  },
  done_full: {
    label: 'Выполнена полностью',
    className: 'bg-ndvi-good/20 text-ndvi-good',
  },
  done_partial: {
    label: 'Выполнена частично',
    className: 'bg-accent-brand/15 text-accent-brand',
  },
  cancelled: {
    label: 'Отменена',
    className: 'bg-muted text-muted-foreground line-through',
  },
};

const CHIP_BASE =
  'inline-flex items-center rounded-md border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap';

export function OpStatusBadge({ status, rescheduled }: Props) {
  if (!status) return null;
  const s = STATUS_MAP[status] ?? {
    label: status,
    className: 'bg-muted text-foreground',
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn(CHIP_BASE, s.className)}>{s.label}</span>
      {rescheduled && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                CHIP_BASE,
                'bg-accent-brand/15 text-accent-brand cursor-help',
              )}
            >
              Перенесена
            </span>
          </TooltipTrigger>
          <TooltipContent>Плановая дата переносилась</TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}

export default OpStatusBadge;
